import { WALLET_ADAPTERS } from '@web3auth/base';
import { useWeb3Auth } from '@web3auth/no-modal-react-hooks';
import { ethers, Signer } from 'ethers';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { isDummyLogin } from 'services/api/utils';

type Web3Response = {
  address: string | null;
  email: string | null;
  isInitialized: boolean;
  isInitializing: boolean;
  disconnect: () => void;
  signMessage: (message: string) => Promise<string>;
  connect: (email: string) => Promise<void>;
  signer: Signer | undefined;
  chainId: number | undefined;
};

const Context = createContext<Web3Response | undefined>(undefined);

export function useWeb3() {
  const context = useContext(Context);
  if (!context) throw new Error('Web3 Provider Not Found');
  return context;
}

export function Web3Context({ children }: { children: ReactNode }) {
  const {
    provider,
    web3Auth,
    authenticateUser,
    userInfo,
    connectTo,
    isInitialized,
    isInitializing,
  } = useWeb3Auth();

  const { t } = useTranslation();
  const [address, setAddress] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [signer, setSigner] = useState<Signer>();
  const [chainId, setChainId] = useState<number>();
  const { openNotification } = useFloatNotification();

  const dummyLogin = isDummyLogin();
  const DUMMY_ADDRESS = '0x0000000000000000000000000000000000000000';
  const initPromiseRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (dummyLogin) setAddress(DUMMY_ADDRESS);
  }, [dummyLogin]);

  const ensureWeb3AuthInitialized = useCallback(async () => {
    if (dummyLogin) return;
    if (!web3Auth) throw new Error('Web3Auth instance not available');
    if (isInitialized) return;
    if (!initPromiseRef.current) {
      initPromiseRef.current = web3Auth.init().finally(() => {
        initPromiseRef.current = null;
      });
    }

    await initPromiseRef.current;
  }, [web3Auth, isInitialized, dummyLogin]);

  useEffect(() => {
    if (dummyLogin) return;
    if (!isInitialized || !provider) return;
    authenticateUser().catch(() => {});
  }, [authenticateUser, isInitialized, provider, dummyLogin]);

  useEffect(() => {
    if (dummyLogin) return;
    if (userInfo && provider) {
      setEmail(userInfo.email || null);
      const ethersProvider = new ethers.providers.Web3Provider(provider);
      const nextSigner = ethersProvider.getSigner();

      nextSigner
        .getAddress()
        .then(setAddress)
        .catch(() => setAddress(null));
      setSigner(nextSigner);

      const rawChainId = (provider as any)?.chainId;
      setChainId(
        typeof rawChainId === 'string' ? Number(rawChainId) : rawChainId
      );
    }
  }, [provider, userInfo, dummyLogin]);

  const connect = useCallback(
    async (email: string) => {
      if (dummyLogin) return;
      try {
        await ensureWeb3AuthInitialized();

        await connectTo(WALLET_ADAPTERS.AUTH, {
          loginProvider: 'email_passwordless',
          extraLoginOptions: { login_hint: email.trim() },
        });
      } catch (error) {
        console.error('Web3 connect error:', error);
        openNotification(
          t('error'),
          t('login-info.login-error-connecting'),
          'error'
        );
      }
    },
    [connectTo, openNotification, t, dummyLogin, ensureWeb3AuthInitialized]
  );

  const disconnect = useCallback(async () => {
    if (dummyLogin) {
      setAddress(null);
      return;
    }

    try {
      setAddress(null);
      setEmail(null);
      setSigner(undefined);
      setChainId(undefined);

      if (web3Auth?.provider) {
        await web3Auth.logout();
      } else {
        openNotification(
          t('error'),
          t('logout.logout-error-provider'),
          'error'
        );
      }
    } catch {
      openNotification(t('error'), t('logout.logout-error-try'), 'error');
    }
  }, [web3Auth, openNotification, t, dummyLogin]);

  const signMessage = useCallback(
    async (message: string): Promise<string> => {
      if (dummyLogin) return '0xdummysignature';
      if (!provider) throw new Error('No provider');
      const ethersProvider = new ethers.providers.Web3Provider(provider);
      const signer = ethersProvider.getSigner();
      return signer.signMessage(message);
    },
    [provider, dummyLogin]
  );

  const value: Web3Response = {
    address,
    isInitialized: dummyLogin ? true : isInitialized,
    isInitializing: dummyLogin ? false : isInitializing,
    email,
    disconnect,
    signMessage,
    connect,
    signer,
    chainId,
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
}
